(function () {
  let uniqueIdCounter = 0;

  /**
   * Generates a unique ID by combining timestamp with an incrementing counter
   * @returns {string} Unique ID string
   */
  function generateUniqueId() {
    uniqueIdCounter++;
    return `select-${Date.now()}-${uniqueIdCounter}-${Math.random().toString(36).substring(2, 9)}`;
  }

  /**
   * @param {ShadowRoot} shadowRoot
   * @param {string} selector
   * @param {number} maxAttempts
   * @param {number} interval
   */
  function waitForElementsInShadowRoot(shadowRoot, selector, maxAttempts = 20, interval = 50) {
    return new Promise((resolve) => {
      if (!shadowRoot) {
        resolve(null);
        return;
      }

      const elements = shadowRoot.querySelectorAll(selector);
      if (elements.length > 0) {
        resolve(elements);
        return;
      }

      const observer = new MutationObserver(() => {
        const foundElements = shadowRoot.querySelectorAll(selector);
        if (foundElements.length > 0) {
          observer.disconnect();
          clearInterval(pollInterval);
          resolve(foundElements);
        }
      });

      observer.observe(shadowRoot, {
        childList: true,
        subtree: true,
      });

      let attempts = 0;
      const pollInterval = setInterval(() => {
        attempts++;
        const foundElements = shadowRoot.querySelectorAll(selector);
        if (foundElements.length > 0) {
          observer.disconnect();
          clearInterval(pollInterval);
          resolve(foundElements);
        } else if (attempts >= maxAttempts) {
          observer.disconnect();
          clearInterval(pollInterval);
          resolve(null);
        }
      }, interval);
    });
  }

  /**
   * @param {Element} element
   * @param {number} maxAttempts
   * @param {number} interval
   */
  function waitForShadowRoot(element, maxAttempts = 10, interval = 50) {
    return new Promise((resolve) => {
      if (element.shadowRoot) {
        resolve(element.shadowRoot);
        return;
      }

      let attempts = 0;
      const checkInterval = setInterval(() => {
        attempts++;
        if (element.shadowRoot) {
          clearInterval(checkInterval);
          resolve(element.shadowRoot);
        } else if (attempts >= maxAttempts) {
          clearInterval(checkInterval);
          resolve(null);
        }
      }, interval);
    });
  }

  /**
   * @param {ShadowRoot} shadowRoot
   * @param {string} selector
   * @param {number} maxAttempts
   * @param {number} interval
   */
  function waitForElementInShadowRoot(shadowRoot, selector, maxAttempts = 20, interval = 50) {
    return new Promise((resolve) => {
      if (!shadowRoot) {
        resolve(null);
        return;
      }

      const element = shadowRoot.querySelector(selector);
      if (element) {
        resolve(element);
        return;
      }

      const observer = new MutationObserver(() => {
        const foundElement = shadowRoot.querySelector(selector);
        if (foundElement) {
          observer.disconnect();
          clearInterval(pollInterval);
          resolve(foundElement);
        }
      });

      observer.observe(shadowRoot, {
        childList: true,
        subtree: true,
      });

      let attempts = 0;
      const pollInterval = setInterval(() => {
        attempts++;
        const foundElement = shadowRoot.querySelector(selector);
        if (foundElement) {
          observer.disconnect();
          clearInterval(pollInterval);
          resolve(foundElement);
        } else if (attempts >= maxAttempts) {
          observer.disconnect();
          clearInterval(pollInterval);
          resolve(null);
        }
      }, interval);
    });
  }

  /**
   * @param {HTMLSelectElement} selectElement
   * @param {Element|null} parentElement
   */
  function convertSelectToRadioButtons(selectElement, parentElement = null) {
    if (!selectElement || selectElement.tagName !== 'SELECT') {
      return;
    }

    if (selectElement.dataset.convertedToRadio === 'true') {
      return;
    }

    const name = selectElement.name || selectElement.id || 'option';
    const selectId = selectElement.id || generateUniqueId();
    const options = Array.from(selectElement.options);
    const selectedValue = selectElement.value;

    const radioContainer = document.createElement('div');
    radioContainer.className = 'radioOptions';
    radioContainer.dataset.selectId = selectId;

    options.forEach((option, index) => {
      const optionValue = document.createElement('div');
      optionValue.className = 'optionValue';
      const style = document.createElement('style');
      style.id = 'hide-shopify-variant-selector-style';
      style.textContent = `
        #content shopify-variant-selector {
          display: none;
        }
        #content .optionValue label {
          --variant-picker-stroke-color: var(--color-variant-border);
          cursor: pointer;
          display: flex;
          flex: 0 0 calc(3ch + 1.3em);
          align-items: center;
          position: relative;
          padding-block: var(--padding-sm);
          padding-inline: var(--padding-lg);
          border: var(--options-border-width, 2px) solid var(--color-variant-border);
          border-radius: var(--options-border-radius);
          overflow: clip;
          justify-content: center;
          min-height: 42px;
          min-width: fit-content;
          white-space: nowrap;
          background-color: var(--color-variant-background);
          color: var(--color-variant-text);
          padding: var(--padding-xs) var(--variant-option-padding-inline);
          font-size: var(--shadow-font-size-sm);
          font-weight: var(--shadow-font-weight-base);
        }
        #content .optionValue label:focus-visible {
          --variant-picker-stroke-color: var(--color-foreground);
          outline: var(--focus-outline-width) solid var(--color-variant-text);
          outline-offset: var(--focus-outline-offset);
        }
        #content .radioOptions {
          display: flex;
          flex-direction: column;
          gap: var(--gap-sm);
        }
        #content .optionValue input {
          display: none;
        }
        #content .optionValue input:checked + label {
          color: var(--color-selected-variant-text);
          border-color: var(--color-selected-variant-border);
          background-color: var(--color-selected-variant-background);
        }
      `;
      optionValue.appendChild(style);

      const radio = document.createElement('input');
      radio.type = 'radio';
      radio.name = name;
      radio.id = `option-${selectId}-${index}`;
      radio.value = option.value;
      radio.checked = option.selected || option.value === selectedValue;
      const isDisabled = option.disabled || option.textContent.includes('Not available');
      radio.disabled = isDisabled;

      Array.from(option.attributes).forEach((attr) => {
        if (attr.name.startsWith('data-') && attr.value != null && attr.value !== '') {
          radio.setAttribute(attr.name, attr.value);
        }
      });

      const label = document.createElement('label');
      label.htmlFor = radio.id;
      label.textContent = option.textContent.trim();
      label.className = 'option';
      label.setAttribute('part', 'radio');
      label.setAttribute('tabindex', isDisabled ? '-1' : '0');

      label.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          if (!radio.disabled) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
          }
        }
      });

      const updateLabelPart = () => {
        const parts = ['radio'];
        if (radio.checked) {
          parts.push('radio-selected');
        }
        if (isDisabled) {
          parts.push('radio-disabled');
          label.classList.add('disabled');
        }
        label.setAttribute('part', parts.join(' '));
      };

      updateLabelPart();

      radio.addEventListener('change', () => {
        selectElement.value = option.value;

        radioContainer.querySelectorAll('input[type="radio"]').forEach((r) => {
          if (r instanceof HTMLInputElement) {
            const lbl = radioContainer.querySelector(`label[for="${r.id}"]`);
            if (lbl) {
              const parts = ['radio'];
              if (r.checked) {
                parts.push('radio-selected');
              }
              if (lbl.classList.contains('disabled')) {
                parts.push('radio-disabled');
              }
              lbl.setAttribute('part', parts.join(' '));
            }
          }
        });

        const changeEvent = new Event('change', {
          bubbles: true,
          cancelable: true,
        });
        selectElement.dispatchEvent(changeEvent);

        const inputEvent = new Event('input', {
          bubbles: true,
          cancelable: true,
        });
        selectElement.dispatchEvent(inputEvent);
      });

      optionValue.appendChild(radio);
      optionValue.appendChild(label);
      radioContainer.appendChild(optionValue);
    });

    selectElement.style.display = 'none';
    selectElement.setAttribute('aria-hidden', 'true');
    selectElement.setAttribute('tabindex', '-1');
    selectElement.dataset.convertedToRadio = 'true';

    if (parentElement?.parentNode) {
      parentElement?.parentNode?.querySelectorAll('.radioOptions').forEach(/** @type {Element} */ (element) => {
        element.remove();
      });
      parentElement?.parentNode?.insertBefore(radioContainer, parentElement?.nextSibling);
    }
  }

  /**
   * @param {ShadowRoot} shadowRoot
   * @param {Element|null} parentElement
   */
  async function convertSelectsToRadioButtons(shadowRoot, parentElement = null) {
    if (!shadowRoot) {
      return;
    }

    const selectElements = await waitForElementsInShadowRoot(shadowRoot, 'select');
    if (selectElements && selectElements.length > 0) {
      Array.from(selectElements).forEach((selectElement) => {
        if (selectElement instanceof HTMLSelectElement) {
          convertSelectToRadioButtons(selectElement, parentElement);
        }
      });
    }

    const allElements = shadowRoot.querySelectorAll('*');
    const shadowRootPromises = Array.from(allElements)
      .filter((element) => element.shadowRoot)
      .map((element) => {
        const nestedShadowRoot = element.shadowRoot;
        return nestedShadowRoot ? convertSelectsToRadioButtons(nestedShadowRoot) : Promise.resolve();
      });

    await Promise.all(shadowRootPromises);
  }

  /**
   * @param {Element[]} wrappedGiftBlocks
   */
  async function updateGiftOptionsTextWithWait(wrappedGiftBlocks) {
    const promises = Array.from(wrappedGiftBlocks).map(async (wrappedGiftBlock) => {
      const wrappedShadowRoot = await waitForShadowRoot(wrappedGiftBlock);
      if (!wrappedShadowRoot) {
        return;
      }

      const variantSelector = await waitForElementInShadowRoot(wrappedShadowRoot, 'shopify-variant-selector');
      const variantSelectorElement = wrappedShadowRoot.querySelector('shopify-variant-selector');

      if (variantSelector) {
        const variantShadowRoot = await waitForShadowRoot(variantSelector);
        if (variantShadowRoot) {
          await convertSelectsToRadioButtons(variantShadowRoot, variantSelectorElement || null);
        }
      }

      await convertSelectsToRadioButtons(wrappedShadowRoot, variantSelectorElement);
    });

    await Promise.all(promises);
  }

  async function updateGiftOptionsText() {
    const wrappedGiftBlocks = document.querySelectorAll('wrapped-gift-block');

    if (wrappedGiftBlocks.length === 0) {
      return;
    }

    await updateGiftOptionsTextWithWait(Array.from(wrappedGiftBlocks));
  }

  function init() {
    updateGiftOptionsText();

    const observer = new MutationObserver((mutations) => {
      /** @type {Element[]} */
      const newWrappedGiftBlocks = [];

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = /** @type {Element} */ (node);
            if (element.tagName === 'WRAPPED-GIFT-BLOCK') {
              newWrappedGiftBlocks.push(element);
            }
            const nestedBlocks = element.querySelectorAll?.('wrapped-gift-block');
            if (nestedBlocks) {
              newWrappedGiftBlocks.push(...Array.from(nestedBlocks));
            }
          }
        });
      });

      if (newWrappedGiftBlocks.length > 0) {
        updateGiftOptionsTextWithWait(newWrappedGiftBlocks);
      }
    });

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        observer.observe(document.body, {
          childList: true,
          subtree: true,
        });
        setTimeout(() => {
          updateGiftOptionsText();
        }, 500);
      });
    } else {
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
      setTimeout(() => {
        updateGiftOptionsText();
      }, 500);
    }

    document.addEventListener('shopify:variant-selector:ready', updateGiftOptionsText);
  }

  init();
})();
